import { useCallback, useContext, useMemo } from 'react';
import { Search, X } from 'react-feather';
import styled from '@emotion/styled';
import type { ChatContextItem, Chat } from '@trinity/chat';
import { ChatContextType, isBrandInsightsContext } from '@trinity/chat';
import { ActiveChatSessionContext } from '@trinity/chat-components/components/Chat/context';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip';
import { trunc } from '@trinity/chat-components/utils';
import { useHeaderContext } from './context';
import { ContextSearch } from './ContextSearch';
import { ChatContextSearchContainer } from './ContextSearch.Container';

const MAX_NAME_LENGTH = 50;

function ContextSubjectDisplay() {
  const {
    pendingContextType,
    setPendingContextType,
    setPendingContext,
    pendingEverythingSearch,
    setPendingEverythingSearch,
  } = useHeaderContext();
  const session = useContext(ActiveChatSessionContext);

  const handleReset = useCallback(() => {
    setPendingContextType(session.context.type);

    if (session.context.type === ChatContextType.Agent) {
      setPendingContext(session.context.data as ChatContextItem);
    } else {
      setPendingContext(null);
    }
  }, [setPendingContextType, session.context.type, session.context.data, setPendingContext]);

  const handleEverythingSearch = useCallback(() => {
    setPendingEverythingSearch(true);
  }, [setPendingEverythingSearch]);

  const handleAgentSubjectSearch = useCallback(() => {
    setPendingContextType(session.context.type);
    setPendingContext(session.context.data as ChatContextItem);
  }, [setPendingContextType, session.context.type, session.context.data, setPendingContext]);

  const displayName = useMemo(() => {
    return extractNameFromContext(session.context);
  }, [session.context]);

  const searchEnabled = useMemo(() => {
    return (
      isBrandInsightsContext(session.context) ||
      (session.context.type === ChatContextType.Agent && !!session.context.data.subjectAutocomplete?.headerAutocomplete) ||
      session.context.type !== ChatContextType.Agent
    );
  }, [session.context]);

  if (pendingEverythingSearch || pendingContextType) {
    return (
      <ChatContextSearchContainer>
        <StyledContextSearch />
      </ChatContextSearchContainer>
    );
  }

  if (session.context.type === ChatContextType.Global) {
    return <StyledSearchIcon size={19} onClick={handleEverythingSearch} />;
  }

  if (!displayName) {
    if (searchEnabled) {
      return <StyledSearchIcon size={19} onClick={handleAgentSubjectSearch} />;
    }

    if (session.context.type === ChatContextType.KolProfile) {
      return <StyledSearchIcon size={19} onClick={() => setPendingContextType(session.context.type)} />;
    }

    return null;
  }

  return (
    <ContextSubjectWrapper>
      <SubjectLabel name={displayName} onClick={searchEnabled ? handleReset : undefined} />
      {session.context.type !== ChatContextType.Agent && (
        <CloseIconWrapper onClick={handleReset}>
          <CloseIcon size={18} />
        </CloseIconWrapper>
      )}
    </ContextSubjectWrapper>
  );
}

type SubjectLabelProps = {
  name: string;
  onClick?: () => void;
};

function SubjectLabel({ name, onClick }: SubjectLabelProps) {
  if (name.length > MAX_NAME_LENGTH) {
    return (
      <Tooltip title={name}>
        <Subject onClick={onClick}>{trunc(name, MAX_NAME_LENGTH)}</Subject>
      </Tooltip>
    );
  }

  return <Subject onClick={onClick}>{name}</Subject>;
}

function extractNameFromContext(context: Chat.InstanceContextWithMetadata): string | null {
  if (context.type === ChatContextType.Global) {
    return null;
  }

  if (context.type === ChatContextType.Agent) {
    if (context.data?.subject?.name) {
      return context.data.subject.name;
    }
  }

  const metadataName = context.metadata?.name;

  return typeof metadataName === 'string' ? metadataName : null;
}

export { ContextSubjectDisplay };

const StyledContextSearch = styled(ContextSearch)({
  minWidth: 0,
  flex: 1,
});

const ContextSubjectWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  flexShrink: 1,
  overflow: 'hidden',
});

const CloseIcon = styled(X)(({ theme }) => ({
  color: theme.palette.gray.main,
}));

const CloseIconWrapper = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  boxSizing: 'border-box',
  width: 24,
  height: 24,

  cursor: 'pointer',

  marginLeft: 10,

  borderRadius: '50%',

  '&:hover': {
    backgroundColor: theme.palette.gray.light2,

    '& svg': {
      color: theme.palette.black.main,
    },
  },
}));

const StyledSearchIcon = styled(Search)(({ theme }) => ({
  marginLeft: 8,
  cursor: 'pointer',
  color: theme.palette.black.main,
}));

const Subject = styled.div(({ theme }) => ({
  padding: 5,
  boxSizing: 'border-box',
  borderRadius: 4,

  borderStyle: 'solid',
  borderWidth: 2,
  borderColor: 'transparent',

  color: theme.palette.black.main,

  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));
